import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import API, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Stethoscope, ArrowRight, Eye, EyeOff } from "lucide-react";
import InstallAppInline from "@/components/InstallAppInline";

export default function PrescriberLogin() {
  const { checkAuth } = useAuth();
  const navigate = useNavigate();
  const [userId, setUserId] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [mfaChallenge, setMfaChallenge] = useState("");
  const [mfaCode, setMfaCode] = useState("");

  const routeByRole = (role) => {
    if (role === "admin") navigate("/admin");
    else if (role === "prescriber") navigate("/prescriber");
    else if (role === "customer_service") navigate("/prescriber");
    else if (role === "it") navigate("/it");
    else setError("This portal is for staff only.");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await API.post("/auth/employee-login", { user_id: userId, password });
      if (data.mfa_required) {
        setMfaChallenge(data.mfa_challenge);
        return;
      }
      await checkAuth();
      routeByRole(data.role);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await API.post("/auth/verify-mfa", { mfa_challenge: mfaChallenge, code: mfaCode });
      await checkAuth();
      routeByRole(data.role);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4" data-testid="prescriber-login-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 justify-center mb-8">
          <div className="w-10 h-10 rounded-xl bg-purple-600 flex items-center justify-center">
            <Stethoscope className="w-5 h-5 text-white" />
          </div>
          <span className="font-bold text-2xl tracking-tight text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
            GLP<span className="text-purple-400">Flo</span>
          </span>
          <span className="text-slate-500 text-sm ml-1">Staff Portal</span>
        </div>

        <div className="bg-slate-800 rounded-xl border border-slate-700 p-8">
          <h1 className="text-xl font-semibold text-white mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
            {mfaChallenge ? "Two-Factor Authentication" : "Staff Sign In"}
          </h1>
          <p className="text-slate-400 text-sm mb-6">
            {mfaChallenge ? "Enter the 6-digit code from your authenticator app" : "Enter your User ID and password"}
          </p>

          {error && (
            <div className="bg-red-900/30 text-red-400 border border-red-800 rounded-lg px-4 py-3 text-sm mb-4" data-testid="prescriber-login-error">
              {error}
            </div>
          )}

          {!mfaChallenge ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="userId" className="text-sm font-medium text-slate-300">User ID</Label>
                <Input id="userId" value={userId} onChange={(e) => setUserId(e.target.value.toUpperCase())} placeholder=""
                  className="mt-1 rounded-lg bg-slate-700 border-slate-600 text-white placeholder:text-slate-500 focus:ring-purple-500 font-mono tracking-wider" required data-testid="prescriber-userid-input" />
              </div>
              <div>
                <Label htmlFor="password" className="text-sm font-medium text-slate-300">Password</Label>
                <div className="relative mt-1">
                  <Input id="password" type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter password"
                    className="rounded-lg bg-slate-700 border-slate-600 text-white placeholder:text-slate-500 focus:ring-purple-500 pr-10" required data-testid="prescriber-password-input" />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200">
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <Button type="submit" disabled={loading} className="w-full rounded-lg bg-purple-600 hover:bg-purple-700 text-white py-5" data-testid="prescriber-login-submit">
                {loading ? "Signing in..." : "Sign In"} {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
              </Button>
              {/* Staff self-serve password reset. Uses the same /forgot-password
                  flow as patients — the backend looks up by email and sends a
                  reset link that works for any role. Critical fallback for
                  Super Admins who otherwise have no way to recover access
                  (their row on the Team page has no Reset PW button by
                  design, so another admin can't do it for them). */}
              <p className="text-center text-xs text-slate-400">
                Forgot your password?{" "}
                <Link to="/forgot-password" className="text-purple-400 hover:text-purple-300 underline underline-offset-2" data-testid="staff-forgot-password-link">
                  Reset via email
                </Link>
              </p>
            </form>
          ) : (
            <form onSubmit={handleMfaSubmit} className="space-y-4" data-testid="mfa-challenge-form">
              <div>
                <Label htmlFor="mfaCode" className="text-sm font-medium text-slate-300">Authentication Code</Label>
                <Input id="mfaCode" value={mfaCode} onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, "").slice(0, 8))}
                  placeholder="123456"
                  className="mt-1 rounded-lg bg-slate-700 border-slate-600 text-white placeholder:text-slate-500 focus:ring-purple-500 font-mono text-center tracking-[0.4em] text-lg"
                  autoFocus required data-testid="mfa-code-input" />
                <p className="text-[11px] text-slate-500 mt-2">Lost your device? Enter one of your 8-character backup codes.</p>
              </div>
              <Button type="submit" disabled={loading || mfaCode.length < 6} className="w-full rounded-lg bg-purple-600 hover:bg-purple-700 text-white py-5" data-testid="mfa-submit-btn">
                {loading ? "Verifying..." : "Verify"}
              </Button>
              <button type="button" onClick={() => { setMfaChallenge(""); setMfaCode(""); setError(""); }} className="w-full text-center text-xs text-slate-400 hover:text-purple-400" data-testid="mfa-cancel-btn">
                &larr; Sign in with a different account
              </button>
            </form>
          )}

          <div className="mt-6 pt-4 border-t border-slate-700 text-center">
            <Link to="/" className="text-sm text-slate-400 hover:text-purple-400 transition-colors">Back to GLPFlo.com</Link>
          </div>
        </div>

        {/* Embedded install-to-desktop card (replaces old popup) */}
        <div className="mt-4">
          <InstallAppInline portalMode />
        </div>
      </div>
    </div>
  );
}
